declare module 'dayjs' {
  interface Dayjs {
    yyyymmdd(): string;
    nights(end: dayjs.ConfigType): number;
    datesBetween(end: dayjs.ConfigType): string[];
    adiff(
      unit?: dayjs.OpUnitType,
      date?: dayjs.ConfigType,
      float?: false | true | 'ceil' | 'floor' | 'round'
    ): number;
    isBetween(
      start: dayjs.ConfigType,
      end: dayjs.ConfigType,
      c?: dayjs.OpUnitType | null,
      d?: '()' | '[]' | '[)' | '(]'
    ): boolean;
  }
}

import dayjs, { type Dayjs } from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import relativeTime from 'dayjs/plugin/relativeTime';
import utcPlugin from 'dayjs/plugin/utc';
import weekOfYear from 'dayjs/plugin/weekOfYear';

import './nights';
dayjs.extend(utcPlugin);
dayjs.extend(weekOfYear);
dayjs.extend(customParseFormat);
dayjs.extend(relativeTime);
dayjs.extend(isBetween);
dayjs.extend(localizedFormat);

const update: dayjs.PluginFunc = (_, dayjsClass) => {
  dayjsClass.prototype.yyyymmdd = function _yyyymmdd() {
    return this.format('YYYY-MM-DD');
  };
  dayjsClass.prototype.datesBetween = function _datesBetween(
    end: dayjs.ConfigType
  ) {
    return datesBetween(this, end);
  };
  dayjsClass.prototype.adiff = function (unit, date, float) {
    // @ts-ignore
    return adiff(unit, this, date, float);
  };
};
dayjs.extend(update);

export function day(date: dayjs.ConfigType = new Date(), format?: string) {
  return dayjs(date, format);
}

export function strptz(date?: string, at: 'T' | '+' = '+') {
  return date?.split(at)[0];
}

export function utc(date: dayjs.ConfigType = new Date(), format?: string) {
  return dayjs.utc(date, format);
}

export function fromNow(
  date: dayjs.ConfigType = new Date(),
  withoutSuffix?: boolean
) {
  return day(date).fromNow(withoutSuffix);
}

export function fromDate(
  firstDate: dayjs.ConfigType = new Date(),
  secondDate: dayjs.ConfigType = new Date(),
  withoutSuffix?: boolean
) {
  return day(secondDate).from(firstDate, withoutSuffix);
}

export function yyyymmdd(date: string | number | Date | dayjs.Dayjs): string;
export function yyyymmdd(
  date: string | number | Date | dayjs.Dayjs | undefined
): string | undefined;
export function yyyymmdd(
  date: string | number | Date | dayjs.Dayjs | null
): string | null;
export function yyyymmdd(
  date: string | number | Date | dayjs.Dayjs | null | undefined
): string | undefined | null;
export function yyyymmdd(date: any): any {
  if (date === null) return null;
  if (date === undefined) return undefined;
  return day(date).yyyymmdd();
}

export function isDateBetween(
  date: dayjs.ConfigType = new Date(),
  date1: dayjs.ConfigType = new Date(),
  date2: dayjs.ConfigType = new Date()
) {
  return day(date).isBetween(date1, date2);
}

export function adiff(
  unit: dayjs.OpUnitType,
  date: dayjs.ConfigType = new Date(),
  date1: dayjs.ConfigType = new Date(),
  float: false | true | 'ceil' | 'floor' | 'round' = 'floor'
) {
  const diff = Math.abs(day(date).diff(date1, unit, true));
  if (float === true) return diff;
  // @ts-ignore
  return Math[`${float}`](diff);
}

export function datesBetween(
  start: dayjs.ConfigType,
  end?: dayjs.ConfigType,
  options?: {
    ifEndIsBeforeStart?: 'throw' | 'empty' | 'startOnly';
  }
): string[] {
  const ifEndIsBeforeStart = options?.ifEndIsBeforeStart || 'throw';

  if (typeof start === 'string' && start.includes('...')) {
    const [startDate, endDate] = start.split('...');
    start = startDate;
    end = endDate;
  }
  if (!end) end = start;

  if (day(end).isBefore(day(start))) {
    if (ifEndIsBeforeStart === 'throw') {
      throw new Error('End date is before start date');
    } else if (ifEndIsBeforeStart === 'empty') {
      return [];
    } else if (ifEndIsBeforeStart === 'startOnly') {
      return [day(start).yyyymmdd()];
    }
  }

  const dateArray: string[] = [];
  let currentDate = day(start);

  while (!currentDate.isAfter(end)) {
    dateArray.push(day(currentDate).yyyymmdd());
    currentDate = currentDate.add(1, 'day');
  }
  return dateArray;
}

export function isDate(date: string | Date | number): undefined | Dayjs {
  if (!date) return undefined;
  if (date instanceof Date) {
    if (date.toString() === 'Invalid Date' || Number.isNaN(+date))
      return undefined;
    return day(date);
  }

  const d = new Date(date);
  if (d.toString() === 'Invalid Date' || Number.isNaN(+d)) return undefined;

  if (typeof date === 'string') {
    // if smaller than 8, we need to force e.g. 2022-01-01 or 20/20/2022
    if (date.length < 8) return undefined;
    // Regular expression to match date formats
    const dateRegex =
      /^\d{4}[-/]\d{2}[-/]\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|([-+]\d{2}:?\d{2}))?)?$/;
    // Check if the input matches the date format
    if (!dateRegex.test(date)) return undefined;
    return day(d);
  }

  if (typeof date === 'number') {
    if (d.toISOString() < '2000-01-01') return undefined;
    if (d.toISOString() > '2100-01-01') return undefined;
    return day(d);
  }

  throw new Error(`Invalid date type: ${typeof date} / ${date}`);
}
